import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Info } from "lucide-react";

/**
 * ResearchStageNav - the three-stage pipeline that replaces the old tab strip.
 *
 * Navigation state only. It reports the chosen stage upward; it never triggers
 * an analysis, mutates results, or calls an API.
 */
/**
 * `hint` is the same copy the old tab strip showed in its info tooltips, so a
 * visitor who never saw the old UI still learns what stages 2 and 3 do.
 */
const STAGES = [
  { id: "initial", n: 1, label: "Initial Analysis" },
  {
    id: "comparison",
    n: 2,
    label: "Comparative Analysis",
    hint: "Summarize and compare key points from multiple AI responses.",
  },
  {
    id: "final",
    n: 3,
    label: "Final Synthesis",
    hint: "Create one cohesive summary based on the best AI responses.",
  },
];

export default function ResearchStageNav({
  activeStage = "initial",
  onChange,
  hasResults = false,
  comparisonReady = false,
  synthesisReady = false,
  locked = false,
}) {
  const doneMap = {
    initial: hasResults,
    comparison: comparisonReady,
    final: synthesisReady,
  };
  // Which stage's hint is showing. Hover or focus on the info icon; the icon
  // is separate from the stage button so revealing the hint never changes
  // the stage.
  const [hintFor, setHintFor] = useState(null);
  // On a phone the stage rail scrolls sideways, which also clips anything
  // hanging below it, so the hint cannot be a child tooltip there. It is
  // shown instead as a viewport-fixed strip under the rail (QA #32: the
  // icons used to be hidden below 900px altogether). The strip is portalled
  // to <body>: `.main-content` carries a transform on phones, and a fixed
  // element inside a transformed ancestor is positioned against that
  // ancestor, not the viewport. `sheet` holds the strip's top edge while
  // that mode is active.
  const [sheet, setSheet] = useState(null);

  const showHint = (id, el) => {
    const narrow =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(max-width: 900px)").matches;
    if (narrow && el) {
      // Under the rail, not under the icon: the rail has padding below the
      // icon, and a strip hung from the icon overlapped the rail's bottom
      // edge (the rail is sticky and sits above the body).
      const rail = el.closest(".rw-stages");
      const anchor = (rail || el).getBoundingClientRect();
      setSheet({ top: anchor.bottom + 8 });
    } else {
      setSheet(null);
    }
    setHintFor(id);
  };
  const hideHint = () => setHintFor(null);

  // Phone strip: a tap anywhere else, or any scroll, dismisses it (there is
  // no mouseleave on touch, and the strip's position goes stale on scroll).
  useEffect(() => {
    if (!hintFor || !sheet) return undefined;
    const onPointerDown = (e) => {
      const t = e.target;
      if (t && t.closest && (t.closest(".rw-stage-hint") || t.closest(".rw-stage-sheet"))) return;
      setHintFor(null);
    };
    const onScroll = () => setHintFor(null);
    document.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [hintFor, sheet]);

  return (
    <nav className="rw-stages" aria-label="Research stages">
      {STAGES.map((stage, i) => {
        const isActive = activeStage === stage.id;
        // Done stays green while the stage is active too: QA read the blue
        // active pill on a finished Comparative Analysis as "not complete".
        const isDone = doneMap[stage.id];
        const isLocked =
          stage.id !== "initial" && (!hasResults || locked);

        return (
          <React.Fragment key={stage.id}>
            {i > 0 && <span className="rw-stage-sep" aria-hidden="true" />}
            <button
              type="button"
              className={`rw-stage ${isActive ? "is-active" : ""} ${isDone ? "is-done" : ""}`}
              onClick={() => !isLocked && onChange && onChange(stage.id)}
              disabled={isLocked}
              aria-current={isActive ? "step" : undefined}
              title={
                isLocked
                  ? locked
                    ? "Available once the current run finishes"
                    : "Run a research query first"
                  : stage.label
              }
            >
              {isActive && (
                <motion.span
                  layoutId="rw-stage-bg"
                  className="rw-stage-bg"
                  transition={{ duration: 0.26, ease: [0.32, 0.72, 0, 1] }}
                />
              )}
              {/* A stage that is not reachable yet is simply dimmed; it keeps
                  its number rather than a padlock. QA read the padlock as
                  "paid feature" on free and subscribed accounts alike, when
                  it only meant "run the initial analysis first". */}
              <span className="rw-stage-n">
                {isDone ? <Check size={12} /> : stage.n}
              </span>
              <span className="rw-stage-l">{stage.label}</span>
            </button>
            {stage.hint && (
              <span
                className="rw-stage-hint"
                onMouseEnter={(e) => showHint(stage.id, e.currentTarget)}
                onMouseLeave={hideHint}
                onFocus={(e) => showHint(stage.id, e.currentTarget)}
                onBlur={hideHint}
                // Touch: a tap is the only way in. Always show (never
                // toggle) so a tap that also focused the icon does not
                // immediately hide what focus just revealed.
                onClick={(e) => showHint(stage.id, e.currentTarget)}
                tabIndex={0}
                role="img"
                aria-label={stage.hint}
              >
                <Info size={13} aria-hidden="true" />
                <AnimatePresence>
                  {hintFor === stage.id && !sheet && (
                    <motion.span
                      // x keeps the tooltip centred on the icon while the
                      // entry animation owns the transform.
                      initial={{ opacity: 0, x: "-50%", y: -4, scale: 0.95 }}
                      animate={{ opacity: 1, x: "-50%", y: 0, scale: 1 }}
                      exit={{ opacity: 0, x: "-50%", y: -4, scale: 0.95 }}
                      transition={{ duration: 0.18 }}
                      className="tooltip tooltip-tab rw-stage-tip"
                      role="tooltip"
                    >
                      {stage.hint}
                    </motion.span>
                  )}
                </AnimatePresence>
                {hintFor === stage.id &&
                  sheet &&
                  typeof document !== "undefined" &&
                  createPortal(
                    <motion.span
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.18 }}
                      className="rw-stage-sheet"
                      style={{ top: sheet.top }}
                      role="tooltip"
                    >
                      {stage.hint}
                    </motion.span>,
                    document.body,
                  )}
              </span>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}
